import { Account } from '../types';

/**
 * Calculate reconciliation status for an account
 * Compares account balance (external API) with running total (calculated from transactions)
 * Accounts are considered reconciled if the difference is within ±1
 */
export interface ReconciliationData {
  isReconciled: boolean;
  balance: number;
  transactionsTotal: number;
  difference: number;
}

export const calculateReconciliationStatus = (account: Account): ReconciliationData => {
  const difference = account.balance - account.runningTotal;
  const isReconciled = Math.abs(difference) <= 1; // Within ±1 tolerance as requested
  
  return {
    isReconciled,
    balance: account.balance,
    transactionsTotal: account.runningTotal,
    difference
  };
};

/**
 * Calculate reconciliation status for multiple accounts
 * Returns a mapping of account ID to reconciliation data
 */
export const calculateAccountReconciliationData = (accounts: Account[]): {
  [accountId: string]: ReconciliationData;
} => {
  const reconciliationData: { [accountId: string]: ReconciliationData } = {};
  
  accounts.forEach(account => {
    reconciliationData[account.id] = calculateReconciliationStatus(account);
  });
  
  return reconciliationData;
};

/**
 * Get reconciliation summary statistics
 */
export const getReconciliationSummary = (accounts: Account[]) => {
  const totalAccounts = accounts.length;
  const reconciliationData = calculateAccountReconciliationData(accounts);
  
  const reconciledAccounts = Object.values(reconciliationData).filter(
    data => data.isReconciled
  ).length;
  
  const reconciledPercentage = totalAccounts > 0 
    ? Math.round((reconciledAccounts / totalAccounts) * 100) 
    : 0;
  
  return {
    totalAccounts,
    reconciledAccounts,
    unreconciledAccounts: totalAccounts - reconciledAccounts,
    reconciledPercentage
  };
};